import { MedicineSet } from './interfaces/medicineSet.interface';
import { Family, Modified } from './interfaces/family.interface';
import { Agency } from './interfaces/agency.interface';
import { Patent } from './interfaces/patent.interface';
import { Company } from './interfaces/company.interface';
import { User } from './interfaces/user.interface';
import { ResultsState } from './interfaces/resultsState.interface';

/**
 * Generates a MongoDB-compatible ObjectId string (24 hex characters)
 * Format: 4-byte timestamp + 5-byte random + 3-byte counter
 */
export function generateObjectId(): string {
	const timestamp = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
	const randomBytes = Array.from({ length: 10 }, () =>
		Math.floor(Math.random() * 256).toString(16).padStart(2, '0')
	).join('');
	const counter = Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0');
	return timestamp + randomBytes + counter;
}

/*
export const supportedCountries: string[] = [ // Countries from: https://patentscope.wipo.int/search/en/help/data_coverage.jsf
	"AP", "OA", "EP", "EA", "GC",
	"AE", "AR", "AP", "AU", "BH", "BN", "BR", "CA", "CL", "CN", "CO", "CR", "CU", "DE", "DK", "DO", "EC", "EE", "EG", "EP", "ES", "FR", "GB", "GT", "HN", "ID", "IL", "JO", "JP", "KE", "KH", "KR", "MA", "MX", "MY", "NI", "PA", "PE", "PH", "PT", "RU", "SA", "SG", "SV", "TH", "TU", "US", "UY", "VN", "WO", "ZA"
*/

export let supportedCountries: string[] = [
	'WO', 'AP', 'AR', 'AU', 'AT', 'BH', 'BE', 'BR',
	'BN', 'BG', 'KH', 'CA', 'CL', 'CN', 'CO', 'CR',
	'HR', 'CU', 'CZ', 'CS', 'DK', 'DO', 'EC', 'EG',
	'SV', 'EE', 'EA', 'EP', 'FI', 'FR', 'GE', 'DE',
	'DD', 'GR', 'GT', 'HN', 'HU', 'IN', 'ID', 'IE',
	'IL', 'IT', 'JP', 'JO', 'KZ', 'KE', 'LA', 'LV',
	'LT', 'MY', 'MT', 'MX', 'MC', 'MA', 'NL', 'NZ',
	'NI', 'NO', 'PA', 'GC', 'PE', 'PH', 'PL', 'PT',
	'KR', 'RO', 'RU', 'SU', 'SM', 'SA', 'RS', 'SG',
	'SK', 'ZA', 'ES', 'SE', 'CH', 'TH', 'TN', 'TR',
	'AE', 'GB', 'US', 'UY', 'VN'
]; // Static version of Patentscope's coverage, just in case. It will then be overriden with the most recent coverage, just below


if (!window.location.host.includes("localhost")) { // I don't want to do that every time I reload the page in dev... working with the hardcoded version
	fetch("https://patentscope.wipo.int/search/endpoint/autocomplete/en/nationalCollectionsCountries.json")
		.then(response => response.json())
		.then((data: any[]) => {
			supportedCountries = data.map((obj: any) => obj.symbol);
			// console.log(`Got Patentscope data coverage = `, supportedCountries);
		});
}

// const ignoredCountries: string[] = ["VN"]; // Too problematic to resolve;
export const countryGroups = {
	"AP": ["BW", "GM", "GH", "KE", "LS", "MW", "MZ", "NA", "SL", "LR", "RW", "ST", "SO", "SD", "SZ", "TZ", "UG", "ZM", "ZW"], // African Regional Intellectual Property Organization (ARIPO)
	"EA": ["TM", "BY", "TJ", "RU", "KZ", "AZ", "KG", "AM"], // Eurasian Patent Office (EAPO)
	"EP": ["AL", "AT", "BA", "BE", "BG", "CH", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR", "GB", "GR", "HR", "HU", "IE", "IS", "IT", "KH", "LI", "LT", "LU", "LV", "MA", "MC", "MD", "ME", "MK", "MT", "NL", "NO", "NL", "PL", "PT", "RO", "RS", "SE", "SI", "SK", "SM", "TN", "TR"], // European Patent Office (EPO)
	"GC": ["SA", "KW", "AE", "QA", "BH", "OM"], // Cooperation Council For The Arab States Of The Gulf (GCC)
	"OA": ["BJ", "BF", "CM", "CF", "TD", "KM", "CG", "GQ", "GA", "GN", "GW", "CI", "ML", "MR", "NE", "SN", "TG"], // Organisation Africaine de la Propriété Intellectuelle / African Intellectual Property Organization (OAPI)
}
	export const countryGroupKeys: string[] = Object.keys(countryGroups);

export const groupsCCs = ["WO"].concat(countryGroupKeys); // ["WO", "EA", "EP", etc ];

declare const moment: any
declare const CryptoJS: any;
const chut = "~~~~###Adr1anA###~~~~";

export const defaults: {
	user: User,
	company: Company,
	agency: Agency,
	patent: Patent,
	medSet: MedicineSet,
	modified: Modified,
	resultsState: ResultsState
} = {
	user: {
		email: "",
		password: "",
		emailCode: "",
		_company: null,
		_agency: null,
		verified: false,
		enabled: false,
		approval: {
			status: "", // "approved", "rejected"
			date: null,
			by: "" // email address
		},
		phone: "",
		role: 0,
		displayedName: "",
		created: new Date().toISOString(),
		preferences: {
			dateFormat: "month DD, YYYY",
			timeFormat: "24h"
		}
	},
	company: {
		uid: "",
		displayedName: "",
		logoUrl: "",
		website: "",
		emails: [],
		extensions: [],
		modified: []
	},
	agency: {
		uid: "",
		displayedName: "",
		street: "",
		created: new Date().toISOString().substring(0, 10),
		zip: "",
		city: "",
		country: "",
		status: "", // gov, int, non-profit, other
		statusComment: "", // free text
		procureMedicines: "yes",
		procureComment: "", //free text
		letter: "", // URL to the accreditation letter document
		logoUrl: "",
		website: "",

	},
	patent: {
		numbers: {
			application: "",
			publication: "",
			grant: ""
		},
		dates: {
			expiry: null,
			filing: null,
			grant: null,
			publication: null,
		},
		modified: {
			date: "",
			by: ""
		},
		created: {
			date: new Date().toISOString(),
			by: "" // At this time, window.user isn't available yet. I'm defining this value in mas.loadPatent, when I clone the default patent.
		},
		resolution: {
			logs: []
		},
		cc: "WO", // because the first jurisdiction in the list is WO, but if we don't explicitely select another one then WO again, it remains blank in the model,
		customCountry: null,
		policy: "",
		freeText: "",
		pscope: "u",
		isPscopeApproved: false,
		isVirtual: false,
		doPruneTitles: true,
		doResetPatentLogs: true, // Now I always reset the logs, because families are getting too heavy
	},
	medSet: {
		inns: "",
		_company: (window as any)["user"]?.["_company"] || null,
		modified: {
			date: new Date().toISOString(),
			by: ((window as any)["user"]?.email) || ""
		},
		families: []
	},
	modified: {
		date: null,
		by: "Unknown",
		comment: "?"
	},
	resultsState: {
		currentPage: 1,
		skip: 0,
		sortField: "inns",
		sortOrder: 1
	}
}

export const decrypt = (crypted: string): any => {

	const l = `decrypt() - `

	if (typeof crypted !== "string") {
		// console.log(`${l}Data to decrypt = `, crypted)
		throw `${l} - Data to decrypt is not a string!`
	}

	// console.time("decryption took")
	const bytes: any = CryptoJS.AES.decrypt(crypted, chut);
	const originalStringified: string = bytes.toString(CryptoJS.enc.Utf8);

	let decrypted
	try {
		decrypted = JSON.parse(originalStringified);
	} catch (err) {
		// console.log(`Could not JSON.parse decrypted content. Probably not an object. Sending back decrypted string as is.`)
	}
	// console.timeEnd("decryption took")
	return decrypted || originalStringified
}

export const deduplicateStringArray = (input: string[]): string[] => input.filter((item, pos, self) => self.indexOf(item) === pos);


export const resolveNames = (name: string) => {

	// Input: "company" or "companies" or "Company"
	/* output: {
	  ls: "company",  // Lowercase Singular
	  cs: "Company",  // Capitalized Singular
	  lp: "companies" // Lowercase plural
	} */

	if (!name) {
		return { ls: "", cs: "", lp: "" };
	}

	let output: Object
	name = name.toLowerCase()
	let slice3 = name.slice(-3)
	let slice1 = name.slice(-1)

	if (slice3 === "ies") { // companies, families

		let ls = name.replace(/ies$/, "y")

		output = {
			ls: ls,					  // Lowercase Singular: "company", "family"
			cs: capitalize(ls),	  // Capitalized Singular: "Company", "Family"
			lp: name					 // Lowercase plural: "companies", "families"
		}
	} else if (slice1 === "y") { // company, family

		output = {
			ls: name,					  // Lowercase Singular: "company", "family"
			cs: capitalize(name),  // Capitalized Singular: "Company", "Family"
			lp: name.replace(/y$/, "ies")// Lowercase plural: "companies", "families"
		}
	} else if (slice1 === "s") { // diseases, patents, ingredients

		let ls = name.slice(0, -1)

		output = {
			ls: ls,						 // Lowercase Singular: "disease", "ingredient"
			cs: capitalize(ls),	// Capitalized Singular: "Disease", "Ingredient"
			lp: name					// Lowercase plural: "diseases", "ingredients"
		}
	} else { // dicease, patent, ingredient
		output = {
			ls: name,					 // Lowercase Singular: "disease", "ingredient"
			cs: capitalize(name),  // Capitalized Singular: "Disease", "Ingredient"
			lp: name + "s"				// Lowercase plural: "diseases", "ingredients"
		}
	}

	return output
}

export const capitalize = (str: string): string => { // "sAn dieGo" --> "San Diego"
	str = str.toLowerCase()
	return str.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())
}

export const datesToUtc = (dates: Record<string, any>): Record<string, any> => {

	const l = `utils.datesToUtc() - `

	/* dates structure:
	  Object {
		expiry		: null
		filing		: "2011-10-13T22:00:00.000Z"
		grant		: null
		priority	: "2010-10-14T22:00:00.000Z"
		export constation: "2012-04-18T22:00:00.000Z"
	  }
	*/

	// console.log(`\nutils.datesToUtc() - dates received = `, dates)

	let timezoneOffset = new Date().getTimezoneOffset() // -120 for +2H. Number of minutes where the GMT is relative to you


	for (let dateKey of Object.keys(dates)) {

		if (dates[dateKey] === null || dates[dateKey] === undefined) continue // null date

		try {
			dates[dateKey] = moment(dates[dateKey]).add(-timezoneOffset, "minutes").toDate()

			// console.log(`${l}dates[${dateKey}] = `,dates[dateKey])

		} catch (err) {

			continue;
		}
	}

	// console.log(`\nutils.datesToUtc() - dates returned = `, dates)

	return dates
}

export const deepClone = <T>(obj: T): T => { // https://stackoverflow.com/questions/728360/how-do-i-correctly-clone-a-javascript-object

	if (typeof (structuredClone) !== "undefined") {
		return structuredClone(obj) as T // Native JS method, available in Node 17+
	}

	let copy: any;

	// Handle the 3 simple types, and null or undefined
	if (!obj || "object" != typeof obj) return obj;

	// Handle Date
	if (obj instanceof Date) {
		copy = new Date();
		copy.setTime((obj as unknown as Date).getTime());
		return copy as unknown as T;
	}

	// Handle Array
	if (obj instanceof Array) {
		copy = [];
		for (let i = 0, len = obj.length; i < len; i++) {
			copy[i] = deepClone(obj[i]);
		}
		return copy as unknown as T;
	}

	// Handle Object
	if (obj instanceof Object) {
		copy = {};
		for (let attr in obj) {
			if (Object.prototype.hasOwnProperty.call(obj, attr)) copy[attr] = deepClone((obj as any)[attr]);
		}
		return copy as unknown as T;
	}

	throw new Error("Unable to copy obj! Its type isn't supported.");

}

export const getFromPsResponse = (patent: Patent, what: string): string => {

	const l = `getFromPsResponse() - `

	const psResponse = patent.resolution?.patentscopeResponse;

	// WTF patentscopeResponses

	switch (what) {
		case "appNum":
			return psResponse?.applicationNumber
				|| psResponse?.detail?.applicationNumber
				|| psResponse?.detail?.iaNumber
				|| "-";

		case "pubNum":
			return psResponse?.publicationNumber
				|| psResponse?.detail?.publicationNumber
				|| psResponse?.detail?.woFullNumber
				|| "-";
		case "grantNum":
			return psResponse?.grantNumber
				|| psResponse?.detail?.grantNumber
				|| "-";

		case "applicant": {
			const response = patent.resolution?.patentscopeResponse;
			return (response?.applicantsName && response.applicantsName[0])
				|| (response?.detail?.applicants && response.detail.applicants[0]
					&& (response.detail.applicants[0].name || response.detail.applicants[0]));
		}
	}

	return ""
}

const hasOnlyWipoPatent = (f: Family): boolean => {
	return f.patents.length === 1 && f.patents[0].cc === "WO" // Guilherme's note about this logic: if all patents have expired, the WO patent will always remain because it has no expiry date. So I'm removing the family if the only patent remaining is the WO
}

export const isCountrySupported = (cc: string): boolean => {
	const isSupported: boolean = supportedCountries.includes(cc)
	// console.log(`isSupported '${cc}' ? ${isSupported}`)
	return isSupported
}

export const isFamilyAllExpired = (family: Family): boolean => {

	let withoutExpired: Family = removeExpiredPatents(deepClone(family)); // I don't want to alter the original family, I just want to know whether ot not it's expired

	withoutExpired.patents = withoutExpired.patents.filter(p => !p.isVirtual); // Virtual patents don't count in the expired calculations

	if (withoutExpired.patents.length === 0) {
		return true
	}

	if (!hasOnlyWipoPatent(withoutExpired)) { // We have at least the WO patent and another one, so the whole family isn't expired
		return false
	}

	const expiry = family.patents[0].dates?.expiry;
	if (expiry) { // So this is necessarily a WO patent, at this point

		// normally, WO patents don't have an expiry date, but we've stumbled upon some who do (??)

		const expiryMoment = moment(expiry as string | Date);
		if (expiryMoment.isBefore()) {
			return true // Even the WO patent is expired, so yeah the whole family is totally expired
		} else {
			return false // We have a WO patent which has an expiry date (it shouldn't, but... yeah) so the family is not considered expired
		}
	}

	return true // So we have one WO patent, but since they never expire (in theory, they don't have an expiry date), the whole family is considered expired
}

export const isMongoId = (_id: string): boolean => /^[0-9a-fA-F]{24}$/.test(_id);


export const isValid = (key: string, value: string): boolean => {

	// console.log(`Validating ${key}: "${value}"`);

	// if(2>1) return true

	if (!value || !value.length) {
		// console.info(`utils.service isValid(): No value or value length`);
		return false
	}

	switch (key) {
		case "strength":
			return !!value.match(/^\s*[\d\.]{1,5}\s*[a-z]{1,4}\s*$/i) // "50mg", "2.5mg", "1g"
		case "number":
			return !!value.match(/^(?:[A-Z]{1,2})[\dA-Z]{4,12}$/) // "WO2016194813", "WO2014JP63134"
		case "email":
			value = value.toLowerCase()
			let re = /\S+@\S+\.\S+(?:\.\S+)?/ // anystring@anystring.anystring(.anystring?)
			return !!value.match(re)
		case "url":
			let url;
			try {
				url = new URL(value);
			} catch (_) {
				return false;
			}
			return url.protocol.startsWith("http");
		case "pscope":
			try {
				const url = new URL(value);
				if (url.protocol !== "https:") return false;
				if (url.hostname !== "patentscope.wipo.int") return false;
				if (!url.pathname.includes("/search/")) return false;
				if (!url.pathname.endsWith("detail.jsf")) return false;
				const docId = url.searchParams.get("docId");
				return !!docId && !!docId.trim();
			} catch (_) {
				return false;
			}
	}

	return false;
}

export const resolveBgImage = (cc: string): string => {
	return cc ? `url(/assets/images/flags/4x3/${cc.toLowerCase()}.svg)` : ""
}

export const emailToName = (email: string): string => {

	if (!email || typeof email !== 'string') {
		console.error(`Caught error: utils.emailToName: no email! passed=`, email)

		return ""
	}

	return email.split("@")[0]
		.replace(/\./g, " ")
		.split(" ")
		.map(word => word ? word.charAt(0).toUpperCase() + word.slice(1) : "")
		.join(" ")
}

export const preparePatentsForRecheck = (family: Family) => {
	patentsLoop:
	for (let p of family.patents) {
		// These modifications are actually sent to the back-end with addToQueue()...

		if (p.isPscopeApproved) {
			p.approvedLink = "" + p.pscope; // Whenever a link is marked as approved, I back it up here. This field is never reset.
		}

		if (p.doCrosscheckOnly) {
			// new special case (2024-11-08). We want to do the crosscheck only, without resolving the link. This does a little trick, it fails the exclusion rules, so the patent does directly to finishedOnePatent(), where the crosscheck is made
			p.WOcrosscheck = 9; // should not see me
			p.toResolve = true;
			continue patentsLoop
		}

		if (p.cc === "WO") {
			// Always resolving WOs, no matter what. No matter their crosscheck, no matter if they're virtuals or not, no matter if their link is approved. We may want to rebuild virtuals or whatnot.
			p.toResolve = true;
			continue patentsLoop
		}

		if (p.isVirtual && p.WOcrosscheck !== undefined) {
			// In principle, there's no need to recheck the virtuals, since they were built once from the Patentscope API. They would be resolved exactly the same way, it's just a waste of time.
			// However, it's possible that they haven't been upgraded to PAT v3, with WOcrosscheck among other things. In that case I check them
			continue patentsLoop
		}

		if (["WO", "EP", "EA", "AP", "GC", "OA"].includes(p.cc)) {

			// Exception for group patents, which we want to check anyway in order to force build the virtuals (for WO), or propagate EP links to its members.
			// In any case, I'm not resetting the isPscopeApproved flag, even though the patent will be rechecked. So I don't lose the isPscopeApproved info

			p.toResolve = true;
			continue patentsLoop
		}

		if (p.isPscopeApproved) {
			continue patentsLoop // Approved links are immutable
		}

		if (p.pscope === "i") {
			// Nicolas doesn't want his manually ignored links to be resolved again
			continue patentsLoop
		}

		p.toResolve = true;
		p.pscope = "u";
		p.pscopeOrigin = "";
		p.WOcrosscheck = undefined;

	}
}

const removeExpiredPatents = (f: Family): Family => {
	f.patents = f.patents
		.filter(p => {

			const expiry = p["dates"]?.expiry;
			if (expiry === undefined || expiry === null) {
				// Patents with no expiry date at all cannot be considered as expired, and are kept. Their expiry date is just unknown. This keeps WO patents, among others.
				return true
			}

			const dateMoment = moment(expiry as string | Date);

			if (!dateMoment.isValid()) {
				// Patents with no expiry date at all cannot be considered as expired and are kept. Their expiry date is just unknown.
				return true
			}

			return dateMoment.isAfter(); // If the patent is not expired, filter() returns true, so the patent is kept.
		}
		)
	return f
}

export const removeVirtuals = (family: Family) => {

	family.patents = family.patents.filter(p => {

		if (!p.isVirtual) return true; // Keeping all real patents

		if (p.cc !== "WO") return !p.isVirtual; // Virtuals other than WO will return false, discarding them

		/*
			From here on, the only ones remaining are WO virtuals

			Now WO is a special case.

			- If the WO is virtual and was created manually, keep it no matter what
			- If there is just a virtual with no real, it means the virtual has been manually created (by NP, most likely)
				- If there is a real WO: remove the virtual(s)
				- If there is no real WO: keep the virtual
		*/

		if (p.created?.by !== "system") return true; // Keeping manually created WO virtuals

		return !family.patents.find(pWO => pWO.cc === "WO" && !pWO.isVirtual); // If a rWO is found, will return false (discards the vWO), and the other way around
	});

}

export const resetTitlesAndAbstracts = (family: Family) => {
	family.title = [{
		lang: "en",
		content: "r", // Resolving
		cc: "r",
		isCustom: false,
		sourcePatentId: ""
	}];

	family.abstract = [{
		lang: "en",
		content: "r",
		cc: "r",
		isCustom: false,
		sourcePatentId: ""
	}];
}


export const sortByKey = (array: any[], key: string): any[] => {

	// console.log(`\nsortByKey() - received array = `, array)

	let subkey: boolean = key.includes("."), // support for sorting by _company.name
		subkey0: string = "",
		subkey1: string = ""

	if (subkey) {
		let split = key.split(".")
		subkey0 = split[0]
		subkey1 = split[1]
	}
	// console.log(`\nsubkey0 = ${subkey0}, subkey1 = ${subkey1}`)

	return array.sort((a, b) => {

		// console.log(`a = `, a )
		// console.log(`a._company = `, a._company)
		// console.log(`a['_company']['displayedName'] = `, a['_company']['displayedName'])
		let x = subkey ? a[subkey0][subkey1] : a[key],
			y = subkey ? b[subkey0][subkey1] : b[key]
		// console.log(`sortByKey(): x=${x}, y=${y}`)
		return ((x < y) ? -1 : ((x > y) ? 1 : 0));
	});
}

export const sortFamilyPatents = (family: Family): Family => {

	const l = `utils.sortFamilyPatents`

	// SORTING PATENTS: WO patent(s) first, then EP(s), then countries in alphabetical order (same thing as on the server in CSVfamilies.ts)

	// console.log(`${l} Unsorted patents = `, family.patents);

	if (!family.patents) {
		// Can happen if it's a minimalist family, to be displayed in the left column
		// console.log(`${l}No family.patents to sort, aborting`)
		return family
	}

	///console.time("Sorted family in ")

	let sortedGroupPatents: Patent[] = [];

	for (let currentlySorting of groupsCCs) { // Currently sorting: WO
		for (let patent of family.patents) {
			if (patent.cc === currentlySorting) {
				sortedGroupPatents.push(patent);
			}
		}
	}

	// Now we have WO first, then AP, EA, EP etc. Now I remove all these patents from the family and sort the rest alphabetically.

	family.patents = family.patents
		.filter(patent => !groupsCCs.includes(patent.cc))
		.sort((a, b) => a.cc > b.cc ? 1 : ((a._id ?? "") > (b._id ?? "") ? 1 : -1));


	family.patents = sortedGroupPatents.concat(family.patents);

	// console.timeEnd("Sorted family in ") // 169 countries: 0.06ms (!)

	// console.log(`${l} Sorted patents = `, family.patents);

	return family
}


